import React, { useCallback, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Box,
    Button,
    CircularProgress,
    Container,
    TextField,
    Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Alert } from "@material-ui/lab";
import CheckCircleOutlineOutlinedIcon from "@material-ui/icons/CheckCircleOutlineOutlined";
import services from "../../services";
import { useStores } from "../../hooks/use-stores";
import { extractErrorMessage } from "../../utils/helpers";

const GROUP_COUNT = 4;
const GROUP_LENGTH = 4;

const useStyles = makeStyles((theme) => ({
    root: {
        minHeight: "100vh",
        color: "white",
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(8),
    },
    intro: {
        color: "#aaaaaa",
        marginBottom: theme.spacing(3),
    },
    groups: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: theme.spacing(1),
    },
    group: {
        maxWidth: 90,
        "& input": {
            textAlign: "center",
            textTransform: "uppercase",
            letterSpacing: "3px",
            fontSize: "1.1rem",
            color: "white",
        },
    },
    dash: {
        color: "#666666",
    },
    submit: {
        marginTop: theme.spacing(4),
        borderRadius: 22,
    },
    successBox: {
        textAlign: "center",
        padding: theme.spacing(4),
    },
}));

const walletHomePath = (pathname = "") => {
    if (pathname.includes("/royalstarfish")) return "/royalstarfish/wallet";
    if (pathname.includes("/starfish")) return "/starfish/wallet";
    return "/wallet";
};

const RedeemStarfishGiftCard = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const { shoppingBossStore, statStore } = useStores();

    const [groups, setGroups] = useState(Array(GROUP_COUNT).fill(""));
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [capWarning, setCapWarning] = useState(null);
    const [success, setSuccess] = useState(null);

    const inputRefs = useRef([]);

    const code = useMemo(() => groups.join(""), [groups]);
    const isComplete = code.length === GROUP_COUNT * GROUP_LENGTH;

    const applyValue = useCallback((rawValue, startIndex) => {
        // Accept a full pasted code as readily as single keystrokes.
        const cleaned = (rawValue || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

        setGroups((current) => {
            const next = [...current];
            let cursor = 0;

            for (let i = startIndex; i < GROUP_COUNT && cursor < cleaned.length; i++) {
                next[i] = cleaned.slice(cursor, cursor + GROUP_LENGTH);
                cursor += GROUP_LENGTH;
            }

            const lastFilled = next.findIndex((group) => group.length < GROUP_LENGTH);
            const focusIndex = lastFilled === -1 ? GROUP_COUNT - 1 : lastFilled;
            inputRefs.current[focusIndex]?.focus();

            return next;
        });

        setError(null);
        setCapWarning(null);
    }, []);

    const handleKeyDown = useCallback((event, index) => {
        if (event.key === "Backspace" && !groups[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    }, [groups]);

    const redeem = useCallback(async () => {
        setSubmitting(true);
        setError(null);
        setCapWarning(null);

        try {
            // Cheap checksum check first so an obvious typo does not burn one of the
            // member's limited redemption attempts.
            const { valid } = await services.StarfishGiftCards.validate(code);
            if (!valid) {
                setError(t("starfish:redeemGiftCard.invalidCode"));
                return;
            }

            const result = await services.StarfishGiftCards.redeem(code);
            setGroups(Array(GROUP_COUNT).fill(""));

            // Refresh first so the confirmation shows the same shopping balance as the
            // home banner (eWallet + perks), not the eWallet-only figure from SQL.
            await Promise.all([
                shoppingBossStore.fetchUserBalance(),
                statStore.loadStatistics?.(),
            ].filter(Boolean));

            setSuccess(result);
        } catch (e) {
            const response = e?.response?.data;
            if (response?.message === "walletCapExceeded") {
                setCapWarning(response);
            } else {
                setError(extractErrorMessage(e));
            }
        } finally {
            setSubmitting(false);
        }
    }, [code, t, shoppingBossStore, statStore]);

    if (success) {
        const displayBalance = shoppingBossStore.userBalance != null
            ? `$${Number(shoppingBossStore.userBalance).toFixed(2)}`
            : `$${Number(success.walletBalance).toFixed(2)}`;

        return (
            <Container maxWidth="sm" className={classes.root}>
                <Box className={classes.successBox}>
                    <CheckCircleOutlineOutlinedIcon style={{ color: "#007BFF", fontSize: 70 }} />
                    <Typography variant="h6" style={{ marginTop: 16 }}>
                        {t("starfish:redeemGiftCard.successTitle")}
                    </Typography>
                    <Typography variant="h4" style={{ marginTop: 16 }}>
                        {`$${Number(success.creditedAmount).toFixed(2)} ${success.currencyCode}`}
                    </Typography>
                    {/* Shown only when the card was issued in the other currency. */}
                    {!!success.exchangeRate && (
                        <Typography variant="body2" style={{ color: "#aaaaaa", marginTop: 8 }}>
                            {t("starfish:redeemGiftCard.converted", { rate: success.exchangeRate })}
                        </Typography>
                    )}
                    <Typography variant="body1" style={{ marginTop: 24 }}>
                        {t("starfish:redeemGiftCard.newBalance", {
                            balance: displayBalance,
                        })}
                    </Typography>
                    <Button
                        fullWidth
                        color="primary"
                        variant="contained"
                        className={classes.submit}
                        onClick={() => history.push(walletHomePath(location.pathname))}
                    >
                        {t("starfish:redeemGiftCard.backToWallet")}
                    </Button>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="sm" className={classes.root}>
            <Typography variant="h6">{t("starfish:redeemGiftCard.title")}</Typography>
            <Typography variant="body2" className={classes.intro}>
                {t("starfish:redeemGiftCard.intro")}
            </Typography>

            {!!error && <Alert severity="error" variant="outlined" style={{ marginBottom: 16 }}>{error}</Alert>}

            {!!capWarning && (
                <Alert severity="warning" variant="outlined" style={{ marginBottom: 16 }}>
                    {t("starfish:redeemGiftCard.walletCapExceeded", {
                        amount: `$${Number(capWarning.creditedAmount).toFixed(2)}`,
                        headroom: `$${Number(capWarning.walletHeadroom).toFixed(2)}`,
                    })}
                </Alert>
            )}

            <Box className={classes.groups}>
                {groups.map((group, index) => (
                    <React.Fragment key={index}>
                        {index > 0 && <span className={classes.dash}>&ndash;</span>}
                        <TextField
                            className={classes.group}
                            variant="outlined"
                            value={group}
                            inputProps={{ maxLength: GROUP_LENGTH, "aria-label": `Code group ${index + 1}` }}
                            inputRef={(element) => { inputRefs.current[index] = element; }}
                            onChange={(event) => applyValue(event.target.value, index)}
                            onKeyDown={(event) => handleKeyDown(event, index)}
                        />
                    </React.Fragment>
                ))}
            </Box>

            <Button
                fullWidth
                color="primary"
                variant="contained"
                className={classes.submit}
                disabled={!isComplete || submitting}
                onClick={redeem}
            >
                {submitting ? <CircularProgress size={24} /> : t("starfish:redeemGiftCard.redeem")}
            </Button>
        </Container>
    );
};

export default observer(RedeemStarfishGiftCard);
